import React, { useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  LinearProgress,
  Divider,
  Stack,
  Chip,
  keyframes,
} from '@mui/material';
import RouterIcon from '@mui/icons-material/Router';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PaymentsIcon from '@mui/icons-material/Payments';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import type { RestP2PProgressEvent, RestP2PPaymentPart } from '../../../../../services/restP2PPaymentService';

/**
 * `verifying` = Pavo cihazından `getSaleResult` ile reconciliation yapılıyor.
 * Bu state'te kullanıcıya "doğrulanıyor..." gösterilir ve modal cancel butonunu
 * gizler — sadece sonuç gelene kadar beklenir (paranoid mode hariç; orada modal
 * zaten settle olmuştur).
 */
export type RestP2PWaitStatus = 'waiting' | 'verifying' | 'success' | 'error' | 'cancelled';

export interface PavoRestP2PWaitModalProps {
  open: boolean;
  status: RestP2PWaitStatus;
  /** 'cash' | 'pos' | 'mixed' */
  paymentMode: 'cash' | 'pos' | 'mixed';
  /**
   * Modal'ın hangi akış kapsamında açıldığını gösterir. Başlık ve gövde
   * metinleri ödeme/iade akışlarında farklılaşır (örn. PYK POS İade ekranında
   * "POS İade İptal Edildi" başlığı). Varsayılan 'payment'.
   */
  flowMode?: 'payment' | 'refund';
  totalAmount: number;
  parts: RestP2PPaymentPart[];
  /** Karma ödeme ilerlemesi (opsiyonel). */
  progress?: RestP2PProgressEvent | null;
  /** Başarılı/hatalı mesaj. */
  message?: string | null;
  deviceName?: string | null;
  /**
   * `runRestP2PFlow` `outcome.errorCode` (örn. `DEVICE_OFFLINE`, `HTTP_503`).
   * "Yine de Devam Et" butonunun mali kayıt uyarısını
   * konteksleştirmek için kullanılır.
   */
  errorCode?: string | null;
  /**
   * Belirtilirse, error state'de (status === 'error') kullanıcıya
   * "Yine de Devam Et" seçeneği sunulur. Çağrıldığında akış
   * fiscal kayıt yapılmadan SQL atomik akışına devam eder
   * (REST entegrasyonundaki "Yazarkasa Bağlı Değil → Evet, Devam Et"
   * davranışının REST-P2P karşılığı).
   * `cancelled` veya `success` durumunda gösterilmez.
   */
  onContinueWithoutDevice?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
}

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.55); }
  70% { box-shadow: 0 0 0 22px rgba(76, 175, 80, 0); }
  100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
`;

const errorPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.55); }
  70% { box-shadow: 0 0 0 22px rgba(244, 67, 54, 0); }
  100% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0); }
`;

const formatTL = (n: number) =>
  `${n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;

const PavoRestP2PWaitModal: React.FC<PavoRestP2PWaitModalProps> = ({
  open,
  status,
  paymentMode,
  flowMode = 'payment',
  totalAmount,
  parts,
  progress,
  message,
  deviceName,
  errorCode,
  onContinueWithoutDevice,
  onCancel,
  onClose,
}) => {
  const isMixed = paymentMode === 'mixed';
  const isRefund = flowMode === 'refund';
  const isWaiting = status === 'waiting';
  const isVerifying = status === 'verifying';
  // Verifying state'inde de "aktif/bekliyor" görseli korunsun (cancel butonu hariç).
  const isPending = isWaiting || isVerifying;
  const isSuccess = status === 'success';
  const isError = status === 'error' || status === 'cancelled';
  // "Yine de Devam Et" butonu yalnızca gerçek hata (error) durumunda
  // gösterilir — kullanıcı tarafından açıkça iptal edildiyse (cancelled)
  // bypass uygun değildir.
  const showContinueWithoutDevice =
    status === 'error' && typeof onContinueWithoutDevice === 'function';

  // UX: Pavo iptali geldiğinde kullanıcıyı modal'da bekletmeye gerek yok;
  // 2.5 sn sonra otomatik kapanıp ana ekrana dönsün. Manual "Kapat" butonu
  // yine de görünür kalır (kullanıcı erken kapatabilir).
  useEffect(() => {
    if (!open) return;
    if (status !== 'cancelled') return;
    if (typeof onClose !== 'function') return;
    const timer = window.setTimeout(() => {
      onClose();
    }, 2500);
    return () => window.clearTimeout(timer);
  }, [open, status, onClose]);

  const statusColor = isSuccess
    ? '#2e7d32'
    : isError
      ? '#c62828'
      : '#1b5e20';

  const currentPartIndex = isMixed && progress ? Math.min(progress.currentIndex, parts.length - 1) : -1;

  return (
    <Modal
      open={open}
      disableEscapeKeyDown
      onClose={() => {}}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.72)',
          backdropFilter: 'blur(3px)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: '#fff',
          borderRadius: 4,
          padding: { xs: 3, sm: 5 },
          minWidth: { xs: 320, sm: 460 },
          maxWidth: 520,
          boxShadow: '0 12px 48px rgba(0, 0, 0, 0.35)',
          outline: 'none',
        }}
      >
        {/* PAVO CANLI rozeti */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 0.75,
            borderRadius: 999,
            bgcolor: isError ? '#ffebee' : '#e8f5e9',
            color: isError ? '#c62828' : '#2e7d32',
            fontWeight: 700,
            fontSize: 14,
            mb: 3,
            animation: isWaiting
              ? `${pulse} 1.8s ease-out infinite`
              : isError
                ? `${errorPulse} 1.8s ease-out infinite`
                : 'none',
          }}
        >
          <RouterIcon fontSize="small" />
          {isError ? 'PAVO HATA' : isSuccess ? 'PAVO TAMAM' : isVerifying ? 'PAVO DOĞRULAMA' : 'PAVO CANLI'}
          {deviceName && <Typography component="span" sx={{ fontWeight: 500, opacity: 0.85, ml: 0.5 }}>• {deviceName}</Typography>}
        </Box>

        {/* İkon */}
        <Box sx={{ mb: 2 }}>
          {isWaiting && (
            <Box
              sx={{
                width: 110,
                height: 110,
                borderRadius: '50%',
                bgcolor: '#f1f8e9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: `${pulse} 1.8s ease-out infinite`,
                border: '3px solid #66bb6a',
              }}
            >
              {paymentMode === 'pos' ? (
                <CreditCardIcon sx={{ fontSize: 60, color: '#2e7d32' }} />
              ) : (
                <PaymentsIcon sx={{ fontSize: 60, color: '#2e7d32' }} />
              )}
            </Box>
          )}
          {isVerifying && (
            <Box
              sx={{
                width: 110,
                height: 110,
                borderRadius: '50%',
                bgcolor: '#e3f2fd',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: `${pulse} 1.8s ease-out infinite`,
                border: '3px solid #64b5f6',
              }}
            >
              <VerifiedUserIcon sx={{ fontSize: 60, color: '#1565c0' }} />
            </Box>
          )}
          {isSuccess && (
            <CheckCircleIcon sx={{ fontSize: 110, color: '#2e7d32' }} />
          )}
          {isError && (
            <ErrorIcon sx={{ fontSize: 110, color: '#c62828' }} />
          )}
        </Box>

        {/* Başlık */}
        <Typography variant="h5" sx={{ fontWeight: 800, color: statusColor, textAlign: 'center', mb: 0.5 }}>
          {isWaiting && (
            isRefund
              ? (paymentMode === 'cash' ? 'Pavo Nakit İade İşleniyor' : 'Pavo POS İade Bekleniyor')
              : (paymentMode === 'cash' ? 'Pavo Fiş Hazırlanıyor' : 'Pavo Ödeme Bekleniyor')
          )}
          {isVerifying && (isRefund ? 'Pavo İade Doğrulanıyor' : 'Pavo Doğrulanıyor')}
          {isSuccess && (
            isRefund
              ? (paymentMode === 'cash' ? 'Nakit İade Tamamlandı' : 'POS İadesi Tamamlandı')
              : (paymentMode === 'cash' ? 'Fiş Başarıyla Kesildi' : 'Ödeme Başarıyla Alındı')
          )}
          {status === 'error' && (
            isRefund
              ? (paymentMode === 'cash' ? 'Nakit İade Başarısız' : 'POS İadesi Başarısız')
              : (paymentMode === 'cash' ? 'Fiş Kesilemedi' : 'Ödeme Başarısız')
          )}
          {status === 'cancelled' && (
            isRefund
              ? (paymentMode === 'cash' ? 'Nakit İade İptal Edildi' : 'POS İadesi İptal Edildi')
              : (paymentMode === 'cash' ? 'Fiş İptal Edildi' : 'Ödeme İptal Edildi')
          )}
        </Typography>

        <Typography variant="body1" sx={{ color: 'text.secondary', textAlign: 'center', mb: 2 }}>
          {message ||
            (isVerifying
              ? (isRefund
                  ? 'Pavo cihazından iade durumu doğrulanıyor, lütfen bekleyin...'
                  : (paymentMode === 'cash'
                    ? 'Pavo cihazından fiş durumu doğrulanıyor, lütfen bekleyin...'
                    : 'Pavo cihazından ödeme durumu doğrulanıyor, lütfen bekleyin...'))
              : isWaiting
                ? isMixed
                  ? 'Kasiyer Pavo cihazından sıradaki ödemeyi alıyor...'
                  : isRefund
                    ? (paymentMode === 'cash'
                      ? 'Pavo cihazında nakit iade fişi yazdırılıyor...'
                      : 'Pavo cihazında POS iadesi alınıyor, lütfen bekleyin...')
                    : (paymentMode === 'cash'
                      ? 'Nakit kasada alındı, Pavo cihazında mali fiş yazdırılıyor...'
                      : 'Kasiyer Pavo üzerinden kartı okutuyor...')
                : '')}
        </Typography>

        {/* Tutar */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 0.5 }}>
            TOPLAM TUTAR
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#1b5e20' }}>
            {formatTL(totalAmount)}
          </Typography>
        </Box>

        {/* Karma ödeme parça listesi */}
        {isMixed && parts.length > 0 && (
          <Box sx={{ width: '100%', mb: 2 }}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              PARÇALI ÖDEME SIRASI ({(progress?.currentIndex ?? 0) + (isWaiting ? 0 : 1)}/{parts.length})
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {parts.map((part, idx) => {
                const isCurrent = isWaiting && idx === currentPartIndex;
                const isDone = progress ? idx < progress.currentIndex : !isWaiting && isSuccess;
                const partFailed =
                  progress?.lastPart && progress.lastPart.order === idx && progress.lastPart.status === 'failed';

                const chipColor: 'success' | 'primary' | 'default' | 'error' = partFailed
                  ? 'error'
                  : isDone
                    ? 'success'
                    : isCurrent
                      ? 'primary'
                      : 'default';
                const chipLabel = partFailed
                  ? 'Başarısız'
                  : isDone
                    ? 'Tamam'
                    : isCurrent
                      ? 'Alınıyor...'
                      : 'Bekliyor';

                return (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: isCurrent ? '#e8f5e9' : '#fafafa',
                      border: '1px solid',
                      borderColor: isCurrent ? '#81c784' : partFailed ? '#ef9a9a' : '#e0e0e0',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                      {part.method === 'cash' ? (
                        <PaymentsIcon fontSize="small" color="success" />
                      ) : (
                        <CreditCardIcon fontSize="small" color="primary" />
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {part.label || (part.method === 'cash' ? 'Nakit' : 'POS')}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {formatTL(part.amount)}
                      </Typography>
                    </Box>
                    <Chip size="small" color={chipColor} label={chipLabel} />
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* İlerleme çubuğu */}
        {isPending && (
          <Box sx={{ width: '100%', mb: 2 }}>
            <LinearProgress
              color={isError ? 'error' : isVerifying ? 'info' : 'success'}
              variant={isMixed && progress && !isVerifying ? 'determinate' : 'indeterminate'}
              value={
                isMixed && progress && progress.totalCount > 0 && !isVerifying
                  ? Math.min(100, (progress.currentIndex / progress.totalCount) * 100)
                  : undefined
              }
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        )}

        {/* "Yine de Devam Et" uyarı kutusu (yalnızca error durumunda) */}
        {showContinueWithoutDevice && (
          <Box
            sx={{
              width: '100%',
              mb: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: '#fff8e1',
              border: '1px solid #ffe082',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.2,
            }}
          >
            <WarningAmberIcon sx={{ color: '#f57f17', mt: 0.2 }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#e65100', mb: 0.5 }}>
                Pavo cihazına ulaşılamıyor.
              </Typography>
              <Typography variant="caption" sx={{ color: '#6d4c41', display: 'block' }}>
                Ödemeyi <strong>yazarkasa olmadan</strong> kayıt etmek istiyorsanız aşağıdan devam edebilirsiniz.
                Bu işlem <strong>mali fiş kesilmeden</strong> SQL kayıdı oluşturur.
                {errorCode && (
                  <>
                    <br />
                    <span style={{ opacity: 0.7 }}>Kod: {errorCode}</span>
                  </>
                )}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Butonlar */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', alignItems: 'center' }}>
          {/* Verifying state'inde iptal butonu görünmez — kullanıcı reconciliation bitene kadar bekler. */}
          {isWaiting && onCancel && (
            <Button variant="outlined" color="error" onClick={onCancel} size="large" sx={{ minWidth: 220 }}>
              İptal Et
            </Button>
          )}
          {showContinueWithoutDevice && (
            <Button
              variant="contained"
              color="warning"
              size="large"
              fullWidth
              onClick={onContinueWithoutDevice}
              sx={{ fontWeight: 700, py: 1.4 }}
            >
              ✅ Yine de Devam Et (Mali Kayıt Yok)
            </Button>
          )}
          {!isWaiting && onClose && (
            <Button
              variant={showContinueWithoutDevice ? 'outlined' : 'contained'}
              color={isSuccess ? 'success' : showContinueWithoutDevice ? 'error' : 'primary'}
              onClick={onClose}
              size="large"
              sx={{ minWidth: 220 }}
              fullWidth={showContinueWithoutDevice}
            >
              {showContinueWithoutDevice ? '❌ İptal' : 'Kapat'}
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default PavoRestP2PWaitModal;
